import { FC, useEffect, useMemo, useRef } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { CallIntelligenceWritebackV1, CallIntelTypes } from '@frontend/api-call-intel';
import { hasSchemaACL, isWeaveUser } from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { sentry } from '@frontend/tracking';
import { Chip, PopoverDialog, SpinningLoader, useAlert, usePopoverDialog } from '@frontend/design-system';
import { useCallIntelPmsSyncConfig } from '../../hooks/use-call-intel-pms-sync-config';
import { generateTrackingId } from '../../utils';
import { SummarySyncPopoverContent } from './summary-sync-popover-content';
import { getSummarySyncChipConfigMap } from './utils';

interface SummarySyncChipProps {
  callRecord?: Pick<CallIntelTypes.CallRecord, 'id' | 'person' | 'locationId'>;
  isDemo?: boolean;
}

export const SummarySyncChip: FC<SummarySyncChipProps> = ({ callRecord, isDemo }) => {
  const alerts = useAlert();
  const { t } = useTranslation('call-intelligence');
  const manualSyncTriggeredRef = useRef(false);

  const { isContactPresent, summarySyncStatusResponse, summarySyncMode, isFetchingSummarySync } =
    useCallIntelPmsSyncConfig({
      call: callRecord,
      fetchSyncStatus: true,
      isDemo,
    });

  const isProdEnv = appConfig.BACKEND_API.includes('api.weaveconnect.com');
  const isWeaveUserInDevEnv = !isProdEnv && isWeaveUser();
  const hasManualSyncAccess =
    isDemo ||
    isWeaveUserInDevEnv ||
    hasSchemaACL(callRecord?.locationId || '', Permission.CALL_INTELLIGENCE_WRITEBACK_MANUAL_SYNC);

  const { mutate: initiateManualWritebackMutate, isLoading: isWritebackInProgress } =
    CallIntelligenceWritebackV1.Mutations.useWritebackCallsMutation({
      options: {
        onSuccess: () => {
          manualSyncTriggeredRef.current = true;
        },
        onError: (error) => {
          alerts.error(t('Failed to initiate sync. Please try again.'));
          sentry.warn({
            team: 'analytics',
            topic: 'analytics',
            error: `PMS Summary Sync Error: ${error}`,
            addContext: {
              name: 'PMS Summary Sync Error',
              context: {
                callId: callRecord?.id,
                error,
              },
            },
          });
        },
      },
    });

  const isSyncing = isWritebackInProgress || isFetchingSummarySync;

  const summarySyncChipConfig = useMemo(() => {
    return getSummarySyncChipConfigMap({
      summarySyncStatus: summarySyncStatusResponse?.status,
      isContactMissing: !isContactPresent,
      isSyncing,
      hasManualSyncAccess,
    });
  }, [isContactPresent, summarySyncStatusResponse?.status, hasManualSyncAccess, isSyncing]);

  const { getDialogProps, getTriggerProps, open: openPopover } = usePopoverDialog({ placement: 'bottom-start' });

  const triggerManualSync = () => {
    if (isDemo || hasManualSyncAccess) {
      initiateManualWritebackMutate({
        callId: callRecord?.id || '',
        locationId: callRecord?.locationId || '',
        isDemo,
      });
    }
  };

  const handleChipClick = () => {
    if (isSyncing) {
      return;
    }
    switch (summarySyncStatusResponse?.status) {
      case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_READY_TO_SYNC:
        triggerManualSync();
        break;
      case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_FAILED:
      case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_SUCCEEDED:
        // Open popover to show status details
        openPopover();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!manualSyncTriggeredRef.current || isSyncing) {
      return;
    }
    if (
      summarySyncStatusResponse?.status === CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_SUCCEEDED
    ) {
      alerts.success(t('Summary synced successfully to PMS.'));
      manualSyncTriggeredRef.current = false;
    } else if (
      summarySyncStatusResponse?.status === CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_FAILED
    ) {
      alerts.error(t('Summary sync failed. Please try again.'));
      manualSyncTriggeredRef.current = false;
    }
  }, [alerts, summarySyncStatusResponse?.status, isSyncing, t]);

  if (!summarySyncStatusResponse?.status) {
    return null;
  }

  return (
    <>
      <Chip<'button'>
        leftElement={<SyncIcon iconName={summarySyncChipConfig.iconName} />}
        variant={summarySyncChipConfig.chipVariant}
        maxWidth='240px'
        as='button'
        css={{
          cursor: summarySyncChipConfig.disabled ? 'default' : 'pointer',
          border: `1px solid ${summarySyncChipConfig.borderColor}`,
          backgroundColor: summarySyncChipConfig.backgroundColor,
          color: summarySyncChipConfig.color,
        }}
        {...getTriggerProps()}
        trackingId={generateTrackingId({
          component: 'summary-sync-chip',
          context: 'chip-click',
          isDemoAccount: isDemo,
        })}
        onClick={handleChipClick}
        tooltipLabel={summarySyncChipConfig.tooltipLabel}
        disabled={summarySyncChipConfig.disabled}
        data-testid='summary-sync-chip'
      >
        {summarySyncChipConfig.label}
      </Chip>
      <PopoverDialog {...getDialogProps()} css={{ padding: 0 }}>
        <SummarySyncPopoverContent
          summarySyncResponse={summarySyncStatusResponse}
          summarySyncMode={summarySyncMode}
          onRetrySync={triggerManualSync}
          isDemo={isDemo}
          allowRetrySync={hasManualSyncAccess}
          disableRetry={isSyncing}
        />
      </PopoverDialog>
    </>
  );
};

export const SyncIcon: FC<{ iconName: IconName | 'loader' }> = ({ iconName }) => {
  if (iconName === 'loader') {
    return <SpinningLoader size='xxs' />;
  }
  return <Icon name={iconName} />;
};
